/**
 * HomePage constants and types
 */

import type { AssessmentChecklistItem } from "../../services/assessmentService";
import IconMPR from "../../assets/icon-medicinal-products.svg";
import IconCOS from "../../assets/icon-cosmetics.svg";
import IconMDV from "../../assets/icon-medical-devices.svg";
import IconCHP from "../../assets/icon-chemicals-and-household-products.svg";
import IconFDS from "../../assets/icon-food-and-food-supplements.svg";

/** sessionStorage key for HomePage domain selector (API `domain` header source). */
export const DOMAIN_SESSION_STORAGE_KEY = "gri.regulationsNext.selectedDomain";

/** Domain option for selector */
export interface DomainOption {
  value: string;
  label: string;
  icon: string;
  /** Privilege required to show this domain in the selector. */
  viewPrivilege: string;
}

/** Privilege codes that gate domain dropdown options (`/authz/privileges`). */
export const DOMAIN_VIEW_PRIVILEGES = {
  MPR: "gri.regulation.view_mpr",
  COS: "gri.regulation.view_cos",
  MDV: "gri.regulation.view_mdv",
  CHP: "gri.regulation.view_chp",
  FDS: "gri.regulation.view_fds",
} as const;

/** Domain configuration with icons */
export const DOMAIN_OPTIONS: DomainOption[] = [
  {
    value: "MPR",
    label: "Medicinal Products (MPR)",
    icon: IconMPR,
    viewPrivilege: DOMAIN_VIEW_PRIVILEGES.MPR,
  },
  {
    value: "COS",
    label: "Cosmetics (COS)",
    icon: IconCOS,
    viewPrivilege: DOMAIN_VIEW_PRIVILEGES.COS,
  },
  {
    value: "MDV",
    label: "Medical Devices (MDV)",
    icon: IconMDV,
    viewPrivilege: DOMAIN_VIEW_PRIVILEGES.MDV,
  },
  {
    value: "CHP",
    label: "Chemicals and Household Products (CHP)",
    icon: IconCHP,
    viewPrivilege: DOMAIN_VIEW_PRIVILEGES.CHP,
  },
  {
    value: "FDS",
    label: "Food and Food Supplements (FDS)",
    icon: IconFDS,
    viewPrivilege: DOMAIN_VIEW_PRIVILEGES.FDS,
  },
];

/** Domains the user may select, based on `gri.regulation.view_*` privileges. */
export function filterDomainOptionsByPrivileges(
  privileges: readonly string[],
  options: readonly DomainOption[] = DOMAIN_OPTIONS,
): DomainOption[] {
  const allowed = new Set(privileges);
  return options.filter((option) => allowed.has(option.viewPrivilege));
}

/**
 * Keep the current domain when still allowed; otherwise the first allowed
 * domain; otherwise null when the user has no domain view privileges.
 */
export function resolveAllowedDomain(
  currentDomain: string,
  allowedOptions: readonly DomainOption[],
): string | null {
  if (allowedOptions.some((option) => option.value === currentDomain)) {
    return currentDomain;
  }
  return allowedOptions[0]?.value ?? null;
}
/** UI shape for checklist card (transformed from API) */
export interface ChecklistCardItem {
  id: string;
  name: string;
  description: string;
  conceptsCount: number;
  updatedAt: string;
  concepts: Array<{ title: string; values: string[] }>;
  keywords: string[];
  raw?: AssessmentChecklistItem;
}

/** Transform API checklist item to card UI shape */
export function transformChecklistToCard(
  item: AssessmentChecklistItem
): ChecklistCardItem {
  const concepts = item.concepts?.concepts ?? [];
  const conceptsValuesCount = concepts.reduce(
    (sum, c) => sum + (Array.isArray(c.values) ? c.values.length : 0),
    0
  );
  return {
    id: String(item.id),
    name: item.name,
    description: item.description,
    // "Concepts" count = total number of concept values across all dimensions
    conceptsCount: conceptsValuesCount,
    updatedAt: item.updated_at
      ? new Date(item.updated_at).toLocaleDateString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "",
    concepts: concepts.map((c) => ({
      title: c.concept_type || c.concept_code || "Concept",
      values: (c.values ?? []).map((v) => v.name),
    })),
    keywords: Array.isArray(item.keywords) ? item.keywords : [],
    raw: item,
  };
}
